import { useEffect, useState, useCallback, useRef } from "react"
import { PurchasProvider, usePurchase } from "../../context/purchaseContext"
import { UltimateAlertsProvider } from "../../context/ultimateAlertsContext"
import { useAppState } from "../../context/appStateContext"
import useQueryParams from "../../hooks/useQueryParams"
import useLastExpiredWasUltimate from "../../hooks/useLastExpiredWasUltimate"
import useHasPro from "../../hooks/useHasPro"
import useSwitchToBasicShown from "../../hooks/useSwitchToBasicShown"
import useStrings from "../../localization/useStrings"
import { useExpirationStrings } from "../BubbleExpirationPaywall/useExpirationStrings"
import { formatCurrency, formatProductPrice } from "../../utils/utils"
import svg from "../../utils/svg"
import native from "../../native/native"
import PrimaryButton from "../../components/PrimaryButton/PrimaryButton"
import FixedCloseButton from "../../components/FixedCloseButton/FixedCloseButton"
import LifetimeComingSoonPopup from "../BubbleExpirationPaywall/LifetimeComingSoonPopup/LifetimeComingSoonPopup"
import LifetimeBottomSheet from "../BubbleExpirationPaywall/LifetimeBottomSheet/LifetimeBottomSheet"
import ComparePlansSheet from "../../components/ComparePlansSheet/ComparePlansSheet"
import UltimateSubfeaturesPopups from "../../components/UltimateSubfeaturesPopups/UltimateSubfeaturesPopups"
import styles from "./OneStepExpirationPaywall.module.scss"

const OneStepExpirationPaywall = () => {
  return (
    <PurchasProvider>
      <UltimateAlertsProvider>
        <OneStepExpirationContent />
      </UltimateAlertsProvider>
    </PurchasProvider>
  )
}

type PlanType = "yearly" | "monthly" | "lifetime"

const OneStepExpirationContent = () => {
  const { actions, state: appState } = useAppState()
  const { state: purchaseState, actions: purchaseActions } = usePurchase()
  const queryParams = useQueryParams()
  const strings = useStrings()
  const expirationStrings = useExpirationStrings()
  const lastExpiredSubscriptionWasUltimate = useLastExpiredWasUltimate()
  const hasPro = useHasPro()
  const [selectedPlan, setSelectedPlan] = useState<PlanType>("yearly")
  const [showLifetimeSheet, setShowLifetimeSheet] = useState(false)
  const [showLifetimePopup, setShowLifetimePopup] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const switchToBasicRef = useRef<HTMLButtonElement>(null)
  const fixedFooterRef = useRef<HTMLDivElement>(null)

  const bottomInset = appState.initialData?.originalBottomInset || 0
  const topInset = appState.initialData?.topInset || 0
  const [showScrollHint, setShowScrollHint] = useState(queryParams.scrollHint)
  const [showCloseButton, setShowCloseButton] = useState(false)
  const [fixedFooterHeight, setFixedFooterHeight] = useState<number | null>(
    null,
  )
  const hasSwitchToBasicBeenShown = useSwitchToBasicShown(
    scrollContainerRef,
    switchToBasicRef,
    fixedFooterRef,
  )

  useEffect(() => {
    actions.setHeaderInsetColor("var(--background)")
    actions.setIgnoreFooterInset(true)

    if (lastExpiredSubscriptionWasUltimate) {
      purchaseActions.setUltimateSelected(true)
    }
  }, [actions])

  useEffect(() => {
    if (hasPro) {
      const timer = setTimeout(() => setShowCloseButton(true), 3000)
      return () => clearTimeout(timer)
    }
  }, [hasPro])

  useEffect(() => {
    const delay = queryParams.scrollHint ? 0 : queryParams.scrollHintDelay

    setShowScrollHint(false)

    if (delay === undefined || hasSwitchToBasicBeenShown) {
      return
    }

    const showHintIfStillRelevant = () => {
      if (!hasSwitchToBasicBeenShown) {
        setShowScrollHint(true)
      }
    }

    if (delay === 0) {
      showHintIfStillRelevant()
      return
    }

    const timer = setTimeout(showHintIfStillRelevant, delay * 1000)
    return () => clearTimeout(timer)
  }, [
    queryParams.scrollHint,
    queryParams.scrollHintDelay,
    hasSwitchToBasicBeenShown,
  ])

  useEffect(() => {
    const footer = fixedFooterRef.current
    if (!footer) return

    const updateFooterHeight = () => {
      setFixedFooterHeight(footer.getBoundingClientRect().height)
    }

    updateFooterHeight()
    window.addEventListener("resize", updateFooterHeight)

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(updateFooterHeight)

    resizeObserver?.observe(footer)

    return () => {
      window.removeEventListener("resize", updateFooterHeight)
      resizeObserver?.disconnect()
    }
  }, [bottomInset, queryParams.showAppStoreButton])

  const monthlyProduct = purchaseState.selectedPlan.monthlyProduct
  const yearlyProduct = purchaseState.selectedPlan.yearlyProduct
  const isUltimate = purchaseState.selectedPlan.isUltimate

  const monthlyPrice = formatProductPrice(monthlyProduct)
  const yearlyPrice = formatProductPrice(yearlyProduct)
  const monthlyFromYearly = formatCurrency(
    yearlyProduct.price / 12,
    yearlyProduct.priceLocale,
  )
  const lifetimeMultiplier = isUltimate ? 3.333333 : 5
  const lifetimePrice = formatCurrency(
    Math.round(yearlyProduct.price * lifetimeMultiplier + 0.01) - 0.01,
    yearlyProduct.priceLocale,
  )

  const discount = Math.round(
    (1 - yearlyProduct.price / 12 / monthlyProduct.price) * 100,
  )

  const handleLifetimeClick = useCallback(() => {
    native.postNativeEvent("analyitcs", { name: "html_lifetime_click" })
    if (queryParams.showLifetimeSecondStep) {
      setShowLifetimeSheet(true)
    } else {
      setShowLifetimePopup(true)
    }
  }, [])

  const handleSheetClose = useCallback(() => {
    setShowLifetimeSheet(false)
  }, [])

  const handleSheetCta = useCallback(() => {
    native.logMixpanelEvent("html_lifetime_sheet_cta")
    setShowLifetimePopup(true)
  }, [])

  const handlePopupClose = useCallback(() => {
    setShowLifetimePopup(false)
  }, [])

  const handleSubscribe = (source: "mainCTA" | "appStore" = "mainCTA") => {
    if (queryParams.showAppStoreButton) {
      native.logMixpanelEvent("html_expiration_sub_pressed", { type: source })
    }
    if (selectedPlan === "yearly") {
      purchaseActions.purchaseProduct(yearlyProduct)
    } else if (selectedPlan === "monthly") {
      purchaseActions.purchaseProduct(monthlyProduct)
    } else {
      handleLifetimeClick()
    }
  }

  const handlePlanClick = (plan: PlanType) => {
    if (plan === "lifetime") {
      handleLifetimeClick()
    } else {
      setSelectedPlan(plan)
    }
  }

  const handleSwitchToBasic = () => {
    native.postNativeEvent("close")
  }

  const handleRestorePurchases = () => {
    native.postNativeEvent("already_purchased")
  }

  const handleTermsClick = () => {
    native.postNativeEvent("terms")
  }

  const handlePrivacyClick = () => {
    native.postNativeEvent("privacy")
  }

  const ctaText =
    selectedPlan === "yearly"
      ? strings.bubbleExpiration.subscribeFor(yearlyPrice)
      : selectedPlan === "monthly"
        ? strings.bubbleExpiration.subscribeForMonth(monthlyPrice)
        : strings.bubbleExpiration.lifetime

  const features = isUltimate
    ? [
        {
          id: "ultimate",
          IconComponent: svg.UltimateIcon,
          title: strings.bubbleExpiration.everyUltimateFeature.replace(
            "\n",
            " ",
          ),
          description: strings.bubbleExpiration.unlockUltimateTools,
        },
        {
          id: "cloud",
          IconComponent: svg.featureIcons.CloudVault,
          title: strings.bubbleExpiration.cloudVaultUltimate,
          description: strings.bubbleExpiration.secureBackups,
        },
        {
          id: "space",
          IconComponent: svg.featureIcons.DuplicateDetection,
          title: strings.bubbleExpiration.spaceSaver,
          description: strings.bubbleExpiration.freeUpSpace,
        },
        {
          id: "organization",
          IconComponent: svg.featureIcons.OrganizationPlus,
          title: strings.bubbleExpiration.organizationPlus,
          description: strings.bubbleExpiration.organizationPlusDesc,
        },
        {
          id: "ads",
          IconComponent: svg.featureIcons.Adfree,
          title: strings.bubbleExpiration.noAds,
          description: strings.bubbleExpiration.fullExperience,
        },
      ]
    : [
        {
          id: "premium",
          IconComponent: svg.PremiumIcon,
          title: strings.bubbleExpiration.everyPremiumFeature.replace(
            "\n",
            " ",
          ),
          description: strings.bubbleExpiration.unlockPrivacyTools,
        },
        {
          id: "cloud",
          IconComponent: svg.featureIcons.CloudVault,
          title: strings.bubbleExpiration.cloudVaultGB,
          description: strings.bubbleExpiration.secureBackups,
        },
        {
          id: "space",
          IconComponent: svg.featureIcons.SpaceSaver,
          title: strings.bubbleExpiration.spaceSaver,
          description: strings.bubbleExpiration.freeUpSpace,
        },
        {
          id: "trash",
          IconComponent: svg.featureIcons.Trash,
          title: strings.bubbleExpiration.trashRecovery,
          description: strings.bubbleExpiration.recoverDeleted,
        },
        {
          id: "ads",
          IconComponent: svg.featureIcons.Adfree,
          title: strings.bubbleExpiration.noAds,
          description: strings.bubbleExpiration.fullExperience,
        },
      ]

  return (
    <div className={`${styles.container} ${isUltimate ? styles.ultimate : ""}`}>
      {showCloseButton && (
        <FixedCloseButton
          topInset={topInset + 12}
          onClick={() => native.postNativeEvent("close")}
        />
      )}
      <div
        className={`${styles.scrollContainer} ${queryParams.showAppStoreButton ? styles.scrollContainerWithAppStore : ""}`}
        ref={scrollContainerRef}
      >
        {/* Header */}
        <div className={styles.header}>
          {isUltimate ? (
            <div className={styles.ultimateHeaderIcon}>
              <svg.UltimateIcon />
            </div>
          ) : (
            <svg.PremiumOrangeHeader className={styles.headerIcon} />
          )}
          <label className={styles.title}>
            {expirationStrings.step2.title}
          </label>
          {!queryParams.showPhotosSafeBanner && (
            <label className={styles.subtitle}>
              {isUltimate
                ? strings.bubbleExpiration.oneStepSubtitleUltimate
                : strings.bubbleExpiration.oneStepSubtitle}
            </label>
          )}
        </div>

        {queryParams.showPhotosSafeBanner && (
          <div className={styles.reassuranceBanner}>
            <svg
              className={styles.reassuranceIcon}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            <p className={styles.reassuranceText}>
              Your photos are safe, but critical features are now disabled.
            </p>
          </div>
        )}

        {/* Pricing Cards */}
        <div className={styles.pricingCards}>
          {/* Yearly Card */}
          <button
            className={`${styles.pricingCard} ${selectedPlan === "yearly" ? styles.selected : ""}`}
            onClick={() => handlePlanClick("yearly")}
          >
            <div className={styles.radioCircle}>
              {selectedPlan === "yearly" && (
                <div className={styles.radioFilled} />
              )}
            </div>
            <div className={styles.planInfo}>
              <div className={styles.planNameRow}>
                <span className={styles.planName}>
                  {strings.bubbleExpiration.yearly}
                </span>
                <span className={styles.discountBadge}>-{discount}%</span>
              </div>
              <span className={styles.planSubtext}>
                {strings.bubbleExpiration.billedPerYear(yearlyPrice)}
              </span>
            </div>
            <div className={styles.planPricing}>
              <div className={styles.planPriceRow}>
                <span className={styles.planPrice}>{monthlyFromYearly}</span>
                <span className={styles.planPriceStrikethrough}>
                  {monthlyPrice}
                </span>
              </div>
              <span className={styles.planPeriod}>
                {strings.bubbleExpiration.perMonth}
              </span>
            </div>
          </button>

          {/* Monthly Card */}
          <button
            className={`${styles.pricingCard} ${selectedPlan === "monthly" ? styles.selected : ""}`}
            onClick={() => handlePlanClick("monthly")}
          >
            <div className={styles.radioCircle}>
              {selectedPlan === "monthly" && (
                <div className={styles.radioFilled} />
              )}
            </div>
            <div className={styles.planInfo}>
              <span className={styles.planName}>
                {strings.bubbleExpiration.monthly}
              </span>
            </div>
            <div className={styles.planPricing}>
              <span className={styles.planPrice}>{monthlyPrice}</span>
              <span className={styles.planPeriod}>
                {strings.bubbleExpiration.perMonth}
              </span>
            </div>
          </button>

          {/* Lifetime Card */}
          <button
            className={`${styles.pricingCard} ${selectedPlan === "lifetime" ? styles.selected : ""}`}
            onClick={() => handlePlanClick("lifetime")}
          >
            <div className={styles.radioCircle}>
              {selectedPlan === "lifetime" && (
                <div className={styles.radioFilled} />
              )}
            </div>
            <div className={styles.planInfo}>
              <span className={styles.planName}>
                {strings.bubbleExpiration.lifetime}
              </span>
            </div>
            <div className={styles.planPricing}>
              <span className={styles.planPrice}>{lifetimePrice}</span>
            </div>
          </button>
        </div>

        {/* Features Section */}
        <div className={styles.featuresCard}>
          {features.map((feature) => (
            <div key={feature.id} className={styles.feature}>
              <div className={styles.featureIcon}>
                <feature.IconComponent className={styles.featureSvg} />
              </div>
              <div className={styles.featureContent}>
                <label className={styles.featureTitle}>{feature.title}</label>
                <label className={styles.featureDescription}>
                  {feature.description}
                </label>
              </div>
            </div>
          ))}
        </div>

        {/* Basic Plan Card */}
        <div className={styles.basicSection}>
          <div className={styles.basicCard}>
            <div className={styles.basicHeader}>
              <label className={styles.basicTitle}>
                {expirationStrings.step2.basicSectionTitle}
              </label>
              <span className={styles.freeBadge}>
                {expirationStrings.step2.basicSectionBadge}
              </span>
            </div>
            <label className={styles.basicDescription}>
              {expirationStrings.step2.basicSectionDescription}
            </label>
            <button
              ref={switchToBasicRef}
              className={styles.switchToBasicButton}
              onClick={handleSwitchToBasic}
            >
              {expirationStrings.step2.basicSectionCta}
            </button>
          </div>
        </div>

        {/* Footer Links */}
        <div className={styles.footerLinks}>
          <button className={styles.link} onClick={handleRestorePurchases}>
            {strings.bubbleExpiration.restorePurchases}
          </button>
          <span className={styles.separator}>|</span>
          <button className={styles.link} onClick={handlePrivacyClick}>
            {strings.bubbleExpiration.privacy}
          </button>
          <span className={styles.separator}>|</span>
          <button className={styles.link} onClick={handleTermsClick}>
            {strings.bubbleExpiration.termsOfService}
          </button>
        </div>
      </div>

      {/* Scroll Hint */}
      {showScrollHint && (
        <div
          className={styles.scrollHintOverlay}
          style={
            {
              "--bottom-inset": `${bottomInset}px`,
              "--scroll-hint-bottom":
                fixedFooterHeight === null
                  ? undefined
                  : `${fixedFooterHeight}px`,
            } as React.CSSProperties
          }
        >
          <div className={styles.scrollHint}>
            <span className={styles.scrollHintText}>
              {strings.threeStepExpiration.scrollForMore}
            </span>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M7 2L7 12M7 12L3 8M7 12L11 8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      )}

      {/* Fixed Footer */}
      <div
        ref={fixedFooterRef}
        className={styles.fixedFooter}
        style={
          {
            "--bottom-inset": `${bottomInset}px`,
          } as React.CSSProperties
        }
      >
        <PrimaryButton
          className={styles.subscribeButton}
          onClick={() => handleSubscribe("mainCTA")}
        >
          <span>{ctaText}</span>
          <svg
            className={styles.arrow}
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M12.175 9H0V7H12.175L6.575 1.4L8 0L16 8L8 16L6.575 14.6L12.175 9Z"
              fill="white"
            />
          </svg>
        </PrimaryButton>
        {queryParams.showAppStoreButton && (
          <button
            className={styles.appStoreButton}
            onClick={() => handleSubscribe("appStore")}
          >
            {strings.bubbleExpiration.purchaseWithAppStore}
          </button>
        )}
      </div>

      <LifetimeComingSoonPopup
        visible={showLifetimePopup}
        onClose={handlePopupClose}
      />
      <LifetimeBottomSheet
        visible={showLifetimeSheet}
        onClose={handleSheetClose}
        onCtaClick={handleSheetCta}
      />
      <ComparePlansSheet />
      <UltimateSubfeaturesPopups />
    </div>
  )
}

export default OneStepExpirationPaywall
